import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as _ from "lodash";
import toast from "react-hot-toast";
import { useIsMutating } from "@tanstack/react-query";
import { Button, Skeleton, Typography } from "antd";
import * as Sentry from "@sentry/react";

import {
  useCreateInscriptionOneClick,
  useConfirmInscriptionOneClick,
} from "@/hooks/useTransbank";
import { useAddOneClickUser } from "@/hooks/useAuth";
import { getEnvVariables } from "@/constants/getEnvVariables";
import { useProductStore } from "@/_zustand/store";
import { useMachine } from "@/_zustand/machine";
import { useAuth } from "@/_zustand/auth";
import { useTransbankCredenciales } from "@/hooks/useTransbankCredenciales";
import {
  credencialesCompletas,
  filtrarCredencialesParaBody,
  guardarCredencialesIntento,
  leerCredencialesIntento,
  limpiarCredencialesIntento,
  MSG_SIN_CREDENCIALES_INSCRIPCION,
} from "@/lib/transbankCredenciales";

import card from "@/assets/imgs/tarjeta-de-credito.png";

const { Text } = Typography;

/**
 * Componente unificado para agregar tarjeta OneClick via Transbank.
 *
 * @param {string}    returnPath        - Ruta a la que redirige tras el proceso (ej: '/profile', '/cart')
 * @param {function}  [onSuccess]       - Callback invocado después de agregar la tarjeta correctamente
 * @param {number}    [maquinaId]       - Override de machineSelected (usar en contexto sin tienda, ej: Billetera)
 * @param {string}    [codigoComercio]  - Override de machine.oneclick_codigo_comercio (para contexto Billetera)
 * @param {string}    [apiKey]          - Override de machine.oneclick_apikey (contexto Billetera, AC-4/AC-5)
 * @param {string}    [codigoTienda]    - Override de machine.oneclick_codigo_tienda (contexto Billetera)
 */
export const AddCardOneClick = ({
  returnPath,
  onSuccess,
  maquinaId: maquinaIdProp,
  codigoComercio: codigoComercioProp,
  apiKey: apiKeyProp,
  codigoTienda: codigoTiendaProp,
}) => {
  const query = new URLSearchParams(window.location.search);
  const navigate = useNavigate();

  const { setPayMethod } = useProductStore();
  const { machineSelected, empresa_id } = useMachine();
  const { user, setTempCard, setCards, authType } = useAuth();

  const requestForm = useCreateInscriptionOneClick();
  const finishInscription = useConfirmInscriptionOneClick();
  const addOneClickUser = useAddOneClickUser();
  const credencialesMachine = useTransbankCredenciales("oneclick");

  // Claves correctas de mutación para mostrar el estado de carga
  const isMutatingCreate = useIsMutating({
    mutationKey: ["useCreateInscriptionOneClick"],
  });
  const isMutatingConfirm = useIsMutating({
    mutationKey: ["useConfirmInscriptionOneClick"],
  });

  const { DOMAIN } = getEnvVariables();
  const [formData, setFormData] = useState({ urlWebpay: "", token: "" });
  const [counter, setCounter] = useState(240);

  // Resuelve el maquina_id efectivo: usa el prop si se pasa (contexto Billetera),
  // de lo contrario usa el de la tienda seleccionada (contexto Carrito).
  const effectiveMaquinaId =
    maquinaIdProp != null ? maquinaIdProp : machineSelected;

  // Resuelve las credenciales efectivas: los props (contexto Billetera, AC-4)
  // ganan sobre lo que resuelve useTransbankCredenciales, igual que ya hacía
  // effectiveCodigoComercio con codigoComercioProp.
  const effectiveCodigoComercio =
    codigoComercioProp || credencialesMachine.codeComerce;
  const effectiveApiKey = apiKeyProp || credencialesMachine.apiKey;
  const effectiveCodigoTienda =
    codigoTiendaProp || credencialesMachine.codeStore;
  const effectiveCred = {
    codeComerce: effectiveCodigoComercio,
    apiKey: effectiveApiKey,
    codeStore: effectiveCodigoTienda,
  };

  // Fase 1: Al montar, determinar si hay token de retorno de Webpay o iniciar inscripción
  useEffect(() => {
    const tbkToken = query.get("TBK_TOKEN");

    if (!_.isEmpty(tbkToken)) {
      // Volvemos de Webpay: confirmar la inscripción con LAS MISMAS
      // credenciales usadas en la createInscriptionOneClick que originó este
      // token (AC-5) — sobrevive al remount leyendo el snapshot guardado por
      // clave "insc:"+token; si no existe (TTL vencido/storage bloqueado),
      // cae a la resolución en vivo y reporta un warning.
      let cred = leerCredencialesIntento("insc:" + tbkToken);
      if (!cred) {
        cred = effectiveCred;
        if (credencialesCompletas(cred, "oneclick")) {
          Sentry.captureMessage(
            "Snapshot de credenciales de inscripción OneClick no encontrado, usando resolución en vivo",
            {
              level: "warning",
              tags: { payment_flow: "oneclick" },
              extra: { maquina_id: effectiveMaquinaId },
            },
          );
        }
      }

      finishInscription
        .mutateAsync({
          maquina_id: effectiveMaquinaId,
          token: tbkToken,
          ...filtrarCredencialesParaBody({
            codeComerce: cred.codeComerce,
            apiKey: cred.apiKey,
          }),
        })
        .then((resp) => {
          limpiarCredencialesIntento("insc:" + tbkToken);
          if (resp.response_code === 0) {
            handleInscriptionSuccess(resp, cred);
          } else {
            toast.error("Inscripción rechazada por Webpay");
            navigate(returnPath, { replace: true });
          }
        })
        .catch(() => {
          limpiarCredencialesIntento("insc:" + tbkToken);
          toast.error("Error al confirmar la inscripción");
          navigate(returnPath, { replace: true });
        });
    } else {
      // No hay token: solicitar formulario de inscripción
      getFormInscription();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fase 2: Contador regresivo — cuando llega a 0 renueva el token
  useEffect(() => {
    const timer = setInterval(() => {
      setCounter((prev) => {
        if (prev <= 1) {
          getFormInscription();
          return 240;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const handleInscriptionSuccess = (resp, cred = effectiveCred) => {
    // AC-6: el codigo_comercio con el que se persiste la tarjeta es SIEMPRE
    // el mismo codeComerce enviado a createInscriptionOneClick/
    // confirmInscriptionOneClick (por eso `cred`, no effectiveCodigoComercio
    // "en vivo", que podría haber cambiado tras un remount con otra máquina).
    const codigoComercioInscripcion = cred.codeComerce;

    if (authType === "invitado") {
      // Criterio 6: la respuesta cruda de Transbank no trae codigo_comercio
      // ni maquina_id — sin agregarlos, el filtro de oneclickCards.js (que
      // excluye tarjetas sin codigo_comercio) ocultaría todas las tarjetas
      // de invitado y useDeleteOneClickCard.js caería al fallback configs[0].
      const guestCard = {
        ...resp,
        codigo_comercio: codigoComercioInscripcion,
        maquina_id: effectiveMaquinaId,
      };
      setTempCard(guestCard);
      setCards((prev) => [...(Array.isArray(prev) ? prev : []), guestCard]);
      setPayMethod("oneclick");
      toast.success("Tarjeta agregada correctamente");
      navigate(returnPath, { replace: true });
      onSuccess?.();
      return;
    }

    // Usuario registrado: guardar en la BD
    const dataSend = {
      usuario_id: user.usuario_id,
      usuario_correo: user.usuario_correo,
      authorization_code: resp.authorization_code,
      card_number: resp.card_number,
      card_type: resp.card_type,
      response_code: resp.response_code,
      tbk_user: resp.tbk_user,
      empresa_id: empresa_id,
      maquina_id: effectiveMaquinaId,
      codigo_comercio: codigoComercioInscripcion,
      usuario_oneclick_id: "",
    };

    addOneClickUser
      .mutateAsync(dataSend)
      .then((respOneClick) => {
        if (respOneClick.status === "success") {
          dataSend.usuario_oneclick_id = respOneClick.usuario_oneclick_id;
          setTempCard(dataSend);
          setCards((prev) => [...(Array.isArray(prev) ? prev : []), dataSend]);
          setPayMethod("oneclick");
          toast.success("Tarjeta agregada correctamente");
          navigate(returnPath, { replace: true });
          onSuccess?.();
        } else {
          toast.error("Error al registrar la tarjeta en el sistema");
          navigate(returnPath, { replace: true });
        }
      })
      .catch(() => {
        toast.error("Error en el registro de la tarjeta");
        navigate(returnPath, { replace: true });
      });
  };

  const getFormInscription = () => {
    // P3: sin credenciales resolubles (ej. Billetera con getOneClickCards
    // fallido + machine vacío) no se inicia la inscripción — inscribir contra
    // el comercio genérico produce una tarjeta con 422 garantizado y datos
    // inconsistentes (AC-6).
    if (!credencialesCompletas(effectiveCred, "oneclick")) {
      toast.error(MSG_SIN_CREDENCIALES_INSCRIPCION);
      Sentry.captureMessage(
        "Inscripción OneClick bloqueada por falta de credenciales de comercio",
        {
          level: "error",
          tags: { payment_flow: "oneclick" },
          extra: {
            maquina_id: effectiveMaquinaId,
            tieneCodeComerce: Boolean(effectiveCred.codeComerce),
            tieneApiKey: Boolean(effectiveCred.apiKey),
            tieneCodeStore: Boolean(effectiveCred.codeStore),
          },
        },
      );
      navigate(returnPath, { replace: true });
      return;
    }

    requestForm
      .mutateAsync({
        return_url: `${DOMAIN}${returnPath}`,
        maquina_id: effectiveMaquinaId,
        email: user.usuario_correo,
        ...filtrarCredencialesParaBody({
          codeComerce: effectiveCred.codeComerce,
          apiKey: effectiveCred.apiKey,
        }),
      })
      .then((resp) => {
        guardarCredencialesIntento("insc:" + resp.token, effectiveCred);
        setFormData({ urlWebpay: resp.url_webpay, token: resp.token });
        setCounter(240);
      })
      .catch(() => toast.error("Error al conectar con Webpay"));
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const isLoading =
    isMutatingCreate > 0 || isMutatingConfirm > 0 || !formData.urlWebpay;

  return (
    <div className="p-2">
      <Skeleton loading={isLoading} active>
        <div className="mb-3 mt-2">
          <Text type="secondary" className="inter-regular">
            <strong>Nota:</strong> El tiempo estimado para agregar la tarjeta es
            de 4 minutos.
          </Text>
        </div>

        <form method="post" action={formData.urlWebpay}>
          <input type="hidden" name="TBK_TOKEN" value={formData.token} />
          <Button
            htmlType="submit"
            className="col-12 border border-1 rounded mb-3 p-4 d-flex align-items-center justify-content-between shadow-sm"
            style={{
              height: "auto",
              whiteSpace: "normal",
              backgroundColor: "transparent",
            }}
          >
            <div className="d-flex align-items-center w-100 text-start">
              <img src={card} alt="webpay" width="40" className="img-fluid" />
              <div className="ms-4 flex-grow-1">
                <span className="inter-bold text-black d-block">
                  Agregar nueva tarjeta
                </span>
                <Text size="small" type="secondary">
                  Tiempo restante: {formatTime(counter)}
                </Text>
              </div>
              <span className="text-primary">→</span>
            </div>
          </Button>
        </form>
      </Skeleton>
    </div>
  );
};
